/* eslint-disable no-console */
import { useEffect, useCallback, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { getAuthorizationToken } from "@/auth/tokenStore";

import {
  fetchFirms,
  fetchUsers,
  createThread,
  fetchMessages,
  markMessagesAsRead,
  setSelectedFirm,
  setSelectedUser,
  setUserSearch,
  addOptimisticMessage,
  clearUnreadCount,
  clearNotificationCounts, // NEW
  setCurrentUser,
  setThread,
  openChat,
  closeChat,
  toggleChat,
  minimizeChat,
  maximizeChat,
  updateMessageStatus,
  fetchProviderClient,
  createProviderThread,
  fetchCoPilotUsers,
} from "../../../Redux/new-chat-store/chatSlice";
import wsManager from "../../../Utils/websocket-manager";
import { getToken } from "../../../Utils/helper";
import {
  findLastThreadOrganization,
  getExistingThreadId,
  getOrganizationKey,
  readChatSelection,
  rememberChatSelection,
  selectDefaultChat,
} from "../../../Utils/chatSelection.mjs";

const readLastThread = () => {
  try {
    return JSON.parse(localStorage.getItem("last_thread") || "null");
  } catch {
    return null;
  }
};

export const useChat = ({ skipInitialization = false } = {}) => {
  const dispatch = useDispatch();
  const chatState = useSelector((state) => state.chat2);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(false);
  const connectionTestRef = useRef(false);
  const [userInitialized, setUserInitialized] = useState(false);
  const [organizationsReady, setOrganizationsReady] = useState(false);
  const stateRef = useRef(chatState);
  stateRef.current = chatState;
  const selectionVersion = useRef(0);
  const attemptedOrganizations = useRef(new Set());

  // First useEffect: Initialize user and WebSocket connection
  useEffect(() => {
    if (skipInitialization) return;
    const initializeChat = async () => {
      const token = getAuthorizationToken();
      const pureToken = token?.split(" ")[1];

      if (!token) {
        console.warn("No auth token found for chat connection");
        return;
      }

      console.log("Initializing chat with token:", token.substring(0, 20) + "...");

      try {
        if (!connectionTestRef.current) {
          connectionTestRef.current = true;
          const connectionSuccess = await wsManager.testConnection(pureToken);

          if (connectionSuccess) {
            console.log("Chat connection successful");
          } else {
            console.error("Chat connection failed during initialization");
          }
        }
      } catch (error) {
        console.error("Failed to initialize chat:", error);
      }
    };

    // Only run once to set up user and initialize WebSocket
    if (!userInitialized) {
      console.log("hello");
      // Retrieve user info from localStorage
      const userString = localStorage.getItem("loggedInUserName");
      const userId = localStorage.getItem("loggedInUser");
      const userStr = {
        id: Number(userId),
        name: userString,
      };
      console.log(userStr);
      if (userStr && userStr.id) {
        dispatch(setCurrentUser(userStr));
      }
      setUserInitialized(true);
      initializeChat(); // Initialize WebSocket after user is set
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [dispatch, userInitialized, skipInitialization]);

  const handleSelectFirm = useCallback(
    (firm) => {
      selectionVersion.current += 1;
      attemptedOrganizations.current.clear();
      dispatch(setSelectedFirm(firm));
    },
    [dispatch]
  );

  const sendMessage = useCallback(
    (threadId, content, moreOptions = {}) => {
      const normalizedContent = typeof content === "string" ? content.trim() : "";
      if (!threadId || !normalizedContent) return null;

      console.log(moreOptions);
      const clientMessageId = crypto.randomUUID();
      console.log("Sending message to thread:", threadId);
      console.log(chatState);
      console.log(chatState.currentUser);
      console.log(Object.keys(moreOptions).length > 0);
      // Create optimistic message with proper structure
      const optimisticMessage = {
        client_message_id: clientMessageId,
        id: `temp_${clientMessageId}`,
        content: normalizedContent,
        sender_id: chatState.currentUser?.id,
        sender: {
          id: chatState.currentUser?.id,
          name:
            chatState.currentUser?.name ||
            `${chatState.currentUser?.first_name || ""} ${chatState.currentUser?.last_name || ""}`.trim(),
        },
        sender_name:
          chatState.currentUser?.name ||
          `${chatState.currentUser?.first_name || ""} ${chatState.currentUser?.last_name || ""}`.trim(),
        thread_id: threadId,
        // created_at: new Date().toISOString(),
        status: "sending",
        read: false,
        created_at: new Date().toISOString(),
        ...(Object.keys(moreOptions).length > 0 && {
          context_data: {
            case: {
              case_type: {
                icon_url: moreOptions?.caseTypeIcon,
                name: moreOptions?.caseTypeName,
              },
              incident_date: moreOptions?.incident_date,
            },
            client: {
              first_name: moreOptions?.name,
              last_name: "",
              birthday: moreOptions?.birthday,
              avatar_url: null,
            },
          },
        }),
      };

      // Add optimistic message immediately for instant UI feedback
      dispatch(addOptimisticMessage({ threadId, message: optimisticMessage }));
      const current = stateRef.current;
      if (String(getExistingThreadId(current.selectedUser)) === String(threadId)) {
        rememberChatSelection(
          localStorage,
          current.currentUser?.id || localStorage.getItem("loggedInUser"),
          localStorage.getItem("firm_id"),
          current.selectedFirm,
          threadId
        );
      }

      // Send through WebSocket
      return wsManager.sendMessage(threadId, normalizedContent, clientMessageId, moreOptions);
    },
    [dispatch, chatState.currentUser]
  );

  const handleSelectUser = useCallback(
    async (user) => {
      console.log("Selecting user:", user);
      let userWithThread = { ...user };

      const organization = stateRef.current.selectedFirm;
      const version = ++selectionVersion.current;
      const existingThreadId = getExistingThreadId(user);

      // Check if we need to create a thread
      if (user.case_provider_id && !existingThreadId) {
        try {
          console.log("Creating provider thread for user:", user.id);
          const threadResult = await dispatch(
            createProviderThread({
              caseProviderId: user.case_provider_id,
              organizationKey: getOrganizationKey(organization),
            })
          ).unwrap();

          console.log("Provider thread created:", threadResult);
          userWithThread = {
            ...user,
            ...threadResult,
            threadId: threadResult.threadId || threadResult.thread_id || threadResult.id,
            has_thread: true,
          };
          dispatch(setThread({ threadId: threadResult.id, thread: threadResult }));
        } catch (error) {
          console.error("Failed to create provider thread:", error);
          return;
        }
      } else if (user?.selectedUserIds && user.selectedUserIds.length > 0) {
        try {
          console.log("Creating group thread for user:", user.id);
          const threadResult = await dispatch(
            createThread({
              threadType: "group",
              memberIds: user.selectedUserIds,
              name: user.name || "New Group",
            })
          ).unwrap();

          console.log("Group thread created:", threadResult);
          userWithThread.threadId = threadResult.id;
          dispatch(setThread({ threadId: threadResult.id, thread: threadResult }));
          const initialMessage = user?.group_content?.trim();
          if (initialMessage) {
            sendMessage(threadResult.id, initialMessage);
          }
          userWithThread = { ...userWithThread, ...threadResult, threadId: threadResult.id };
        } catch (error) {
          console.error("Failed to create group thread:", error);
          return;
        }
      } else if (!existingThreadId) {
        try {
          console.log("Creating 1:1 thread for user:", user.id);
          const threadResult = await dispatch(
            createThread({
              threadType: "1:1",
              memberIds: [user.id],
            })
          ).unwrap();

          console.log("1:1 thread created:", threadResult);
          userWithThread.threadId = threadResult.id;
          dispatch(setThread({ threadId: threadResult.id, thread: threadResult }));
        } catch (error) {
          console.error("Failed to create 1:1 thread:", error);
          return;
        }
      } else {
        // Use existing thread ID
        userWithThread.threadId = existingThreadId;
      }

      if (
        version !== selectionVersion.current ||
        getOrganizationKey(organization) !== getOrganizationKey(stateRef.current.selectedFirm)
      )
        return;

      console.log("Setting selected user with thread:", userWithThread);
      dispatch(setSelectedUser(userWithThread));
      rememberChatSelection(
        localStorage,
        stateRef.current.currentUser?.id || localStorage.getItem("loggedInUser"),
        localStorage.getItem("firm_id"),
        organization,
        userWithThread.threadId
      );

      // Load initial messages and clear unread count
      if (userWithThread.threadId) {
        dispatch(fetchMessages({ threadId: userWithThread.threadId }));
        dispatch(clearUnreadCount({ threadId: userWithThread.threadId }));

        //  Also clear notification counts for this thread
        dispatch(clearNotificationCounts({ type: "thread", id: userWithThread.threadId }));
      }
    },
    [dispatch, sendMessage]
  );

  useEffect(() => {
    if (skipInitialization || !userInitialized) return;
    let cancelled = false;
    const version = selectionVersion.current;

    const initializeOrganizations = async () => {
      if (!getToken()) return;
      try {
        const organizations = await dispatch(fetchFirms()).unwrap();
        if (cancelled) return;
        const current = stateRef.current;
        const history = readChatSelection(
          localStorage,
          current.currentUser?.id || localStorage.getItem("loggedInUser"),
          localStorage.getItem("firm_id")
        );
        const selected = organizations.find(
          (organization) =>
            getOrganizationKey(organization) === getOrganizationKey(current.selectedFirm)
        );
        const remembered = organizations.find(
          (organization) => getOrganizationKey(organization) === history.lastOrganization
        );
        if (version === selectionVersion.current) {
          dispatch(
            setSelectedFirm(
              selected ||
                remembered ||
                findLastThreadOrganization(organizations, readLastThread()) ||
                organizations[0] ||
                null
            )
          );
        }
        setOrganizationsReady(true);
      } catch (error) {
        console.error("Failed to load chat organizations:", error);
        if (!cancelled) setOrganizationsReady(true);
      }
    };

    initializeOrganizations();
    return () => {
      cancelled = true;
    };
  }, [dispatch, userInitialized, skipInitialization]);

  useEffect(() => {
    const organization = chatState.selectedFirm;
    if (skipInitialization || !organizationsReady || !organization) return;
    let cancelled = false;
    const version = selectionVersion.current;
    const organizationKey = getOrganizationKey(organization);
    attemptedOrganizations.current.add(organizationKey);

    const loadOrganization = async () => {
      const fetchInbox =
        organization.type === "provider"
          ? fetchProviderClient
          : organization.type === "copilot"
            ? fetchCoPilotUsers
            : fetchUsers;
      try {
        const users = await dispatch(
          fetchInbox({ firmId: organization.id, search: chatState.userSearch })
        ).unwrap();
        if (cancelled || version !== selectionVersion.current) return;
        const current = stateRef.current;
        if (getOrganizationKey(current.selectedFirm) !== organizationKey) return;
        // Filtering the list must not replace a conversation the user already has open.
        if (chatState.userSearch && current.selectedUser) return;
        const history = readChatSelection(
          localStorage,
          current.currentUser?.id || localStorage.getItem("loggedInUser"),
          localStorage.getItem("firm_id")
        );
        const lastThread = readLastThread();
        const lastOrganization = findLastThreadOrganization(current.firms, lastThread);
        const selected = selectDefaultChat(users, [
          getExistingThreadId(current.selectedUser),
          history.byOrganization[organizationKey]?.threadId,
          getOrganizationKey(lastOrganization) === organizationKey ? lastThread?.thread_id : null,
        ]);
        if (selected) {
          await handleSelectUser(selected);
        } else if (!chatState.userSearch) {
          // An empty organization should not strand the user when another has a chat.
          const next = [...current.firms]
            .filter((firm) => !attemptedOrganizations.current.has(getOrganizationKey(firm)))
            .sort(
              (a, b) =>
                (history.byOrganization[getOrganizationKey(b)]?.openedAt || 0) -
                (history.byOrganization[getOrganizationKey(a)]?.openedAt || 0)
            )[0];
          if (next) dispatch(setSelectedFirm(next));
        }
      } catch (error) {
        if (!cancelled) console.error("Failed to open an organization conversation:", error);
      }
    };

    loadOrganization();
    return () => {
      cancelled = true;
    };
  }, [
    chatState.selectedFirm,
    chatState.userSearch,
    dispatch,
    handleSelectUser,
    organizationsReady,
    skipInitialization,
  ]);

  const handleTyping = useCallback((threadId) => {
    if (!lastTypingRef.current) {
      wsManager.markTyping(threadId, true);
      lastTypingRef.current = true;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      wsManager.markTyping(threadId, false);
      lastTypingRef.current = false;
    }, 2000);
  }, []);

  const markAsRead = useCallback(
    (threadId, messageIds) => {
      console.log("Marking messages as read:", { threadId, messageIds });

      // Update local state immediately for instant UI feedback
      dispatch(clearUnreadCount({ threadId }));

      // Also clear notification counts for this thread
      dispatch(clearNotificationCounts({ type: "thread", id: threadId }));

      // Update message read status locally
      messageIds.forEach((messageId) => {
        dispatch(
          updateMessageStatus({
            threadId,
            messageId,
            status: "read",
          })
        );
      });

      // Try WebSocket first, fallback to HTTP API if needed
      const wsSuccess = wsManager.markMessagesAsRead(threadId, messageIds);

      if (!wsSuccess) {
        console.log("WebSocket mark_read failed, falling back to HTTP API");
        // Fallback to HTTP API
        dispatch(markMessagesAsRead({ threadId, messageIds }));
      }
    },
    [dispatch]
  );

  const loadMoreMessages = useCallback(
    (threadId) => {
      const threadMessages = chatState.messages[threadId];
      console.log(threadMessages);
      console.log(threadMessages.oldestMessageId);
      if (threadMessages && threadMessages.oldestMessageId) {
        dispatch(
          fetchMessages({
            threadId,
            before: threadMessages.oldestMessageId,
          })
        );
      }
    },
    [dispatch, chatState.messages]
  );

  const searchUsers = useCallback(
    (query) => {
      dispatch(setUserSearch(query));
    },
    [dispatch]
  );

  // Chat UI controls
  const openChatWindow = useCallback(() => {
    dispatch(openChat());
  }, [dispatch]);

  const closeChatWindow = useCallback(() => {
    dispatch(closeChat());
  }, [dispatch]);

  const toggleChatWindow = useCallback(() => {
    dispatch(toggleChat());
  }, [dispatch]);

  const minimizeChatWindow = useCallback(() => {
    dispatch(minimizeChat());
  }, [dispatch]);

  const maximizeChatWindow = useCallback(() => {
    dispatch(maximizeChat());
  }, [dispatch]);

  // Notification-related functions
  const getNotificationCounts = useCallback(
    (context = {}) => {
      const { firmId, threadId } = context;
      const { notificationCounts } = chatState;

      return {
        global: notificationCounts?.global?.unread ?? 0,
        firm: firmId && notificationCounts.firm ? notificationCounts.firm[firmId] || 0 : 0,
        thread:
          threadId && notificationCounts.thread ? notificationCounts.thread[threadId] || 0 : 0,
      };
    },
    [chatState.notificationCounts]
  );

  const clearNotificationCountsForContext = useCallback(
    (context = {}) => {
      const { type, id } = context; // type: 'global' | 'firm' | 'thread'
      dispatch(clearNotificationCounts({ type, id }));
    },
    [dispatch]
  );

  // Debug functions
  const getConnectionInfo = useCallback(() => {
    return wsManager.getConnectionInfo();
  }, []);

  const reconnectChat = useCallback(async () => {
    const token = getAuthorizationToken();
    const pureToken = token.split(" ")[1];

    if (token) {
      try {
        await wsManager.connect(pureToken);
      } catch (error) {
        console.error("Manual reconnect failed:", error);
      }
    }
  }, []);

  return {
    ...chatState,
    initializing: !skipInitialization && !organizationsReady,
    handleSelectFirm,
    handleSelectUser,
    sendMessage,
    handleTyping,
    markAsRead,
    loadMoreMessages,
    searchUsers,
    openChatWindow,
    closeChatWindow,
    toggleChatWindow,
    minimizeChatWindow,
    maximizeChatWindow,
    // Notification functions
    getNotificationCounts,
    clearNotificationCountsForContext,
    getConnectionInfo,
    reconnectChat,
  };
};
